import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { BriefcaseIcon, UsersIcon, BarChartIcon, ZapIcon, UsersRoundIcon } from 'lucide-react';
const profiles = [{
  icon: UsersRoundIcon,
  title: 'Je groeit liever niet via via',
  desc: 'Je netwerk heeft zijn werk gedaan en nu wil je structureel nieuwe klanten van buitenaf aantrekken. Op eigen kracht, zonder afhankelijk te zijn van wie je kent.'
}, {
  icon: BarChartIcon,
  title: 'B2B',
  desc: 'De klantwaarde ligt hoog genoeg dat meerdere klanten per maand een verschil maken. Je wil dat in een systeem gieten dat consistent aanvragen genereert.'
}, {
  icon: UsersIcon,
  title: 'Vastgelopen in via-via',
  desc: 'Je netwerk droogt op en je wilt structureel nieuwe klanten van buitenaf aantrekken.'
}, {
  icon: ZapIcon,
  title: 'Klaar voor de sonar-aanpak',
  desc: 'Je bent een bedrijf met seriöuse groeiambitie dat gas wil geven.'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};
export function ForWho() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="voor-wie" ref={ref} className="py-20 relative overflow-hidden" style={{
    background: 'linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 100%)'
  }} data-id="element-180">
      <div className="max-w-[1200px] mx-auto px-6" data-id="element-181">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        type: 'spring',
        stiffness: 100
      }} data-id="element-182">
          <div className="section-label" data-id="element-183">Voor wie</div>
          <h2 className="text-[clamp(2.2rem,4vw,3.2rem)] font-black text-brand-dark leading-tight tracking-tight" data-id="element-184">
            CorePrint is voor jou als je
            <br data-id="element-185" />
            <span className="text-brand-primary" data-id="element-186">klaar bent om te groeien</span>
          </h2>
        </motion.div>

        <motion.div className="grid md:grid-cols-2 gap-6 mb-10" variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-187">
          {profiles.map(p => <motion.div key={p.title} className="card group cursor-default" variants={itemVariants} whileHover={{
          y: -6,
          transition: {
            type: 'spring',
            stiffness: 300,
            damping: 20
          }
        }} data-id="element-188">
              <motion.div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{
            background: '#EFF6FF',
            border: '1px solid rgba(0, 82, 204, 0.15)'
          }} whileHover={{
            scale: 1.1,
            rotate: 5,
            transition: {
              type: 'spring',
              stiffness: 300
            }
          }} data-id="element-189">
                <p.icon size={24} className="text-brand-primary" data-id="element-190" />
              </motion.div>
              <h3 className="font-black text-brand-dark text-lg mb-3 group-hover:text-brand-primary transition-colors duration-300" data-id="element-191">
                {p.title}
              </h3>
              <p className="text-brand-gray text-[0.96rem] leading-relaxed" data-id="element-192">
                {p.desc}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Resultaten blok — expertise mix */}
        <motion.div className="rounded-3xl p-8 border border-[#E8EDF8] bg-white mb-10" initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.4,
        type: 'spring'
      }} whileHover={{
        boxShadow: '0 12px 48px rgba(0, 82, 204, 0.1)',
        transition: {
          type: 'spring',
          stiffness: 300,
          damping: 20
        }
      }} data-id="element-193">
          <div className="flex items-start gap-4" data-id="element-194">
            <div className="w-12 h-12 rounded-2xl flex-shrink-0 flex items-center justify-center" style={{
            background: '#EFF6FF',
            border: '1px solid rgba(0, 82, 204, 0.15)'
          }} data-id="element-195">
              <BriefcaseIcon size={22} className="text-brand-primary" data-id="element-196" />
            </div>
            <div data-id="element-197">
              <div className="font-black text-brand-dark text-lg mb-2" data-id="element-198">
                Resultaten die spreken
              </div>
              <p className="text-brand-gray text-[0.96rem] leading-relaxed" data-id="element-199">
                We werken met een mix van experts op het gebied van{' '}
                <strong className="text-brand-dark font-bold" data-id="element-200">
                  adverteren, design, SEO, AI en development
                </strong>
                . Een sterke samenwerking tussen deze vakgebieden zorgt voor de
                hoogste kwaliteit en resultaten die het verschil maken.
              </p>
            </div>
          </div>
        </motion.div>

        {/* CTA Banner */}
        <motion.div className="rounded-[2rem] p-12 text-center relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #0052CC 0%, #0066FF 100%)'
      }} initial={{
        opacity: 0,
        scale: 0.95
      }} animate={inView ? {
        opacity: 1,
        scale: 1
      } : {}} transition={{
        duration: 0.7,
        delay: 0.5,
        type: 'spring',
        stiffness: 100
      }} whileHover={{
        scale: 1.01,
        transition: {
          type: 'spring',
          stiffness: 300,
          damping: 20
        }
      }} data-id="element-201">
          <div className="absolute inset-0 grid-bg opacity-15" data-id="element-202" />
          <div className="relative z-10" data-id="element-203">
            <motion.div className="inline-flex items-center gap-2.5 bg-white/15 rounded-full px-5 py-2 text-white/95 text-sm font-bold mb-6 backdrop-blur-sm" animate={{
            boxShadow: ['0 0 0 0 rgba(255, 255, 255, 0.3)', '0 0 0 8px rgba(255, 255, 255, 0)']
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} data-id="element-204">
              <span className="w-2 h-2 rounded-full bg-[#28C840]" style={{
              boxShadow: '0 0 8px #28C840'
            }} data-id="element-205" />
              Beperkt aantal Early Access plekken beschikbaar
            </motion.div>
            <h3 className="text-3xl md:text-4xl font-black text-white mb-5" data-id="element-206">
              Is jouw markt nog vrij?
            </h3>
            <p className="text-white/80 mb-10 max-w-[520px] mx-auto text-[1.05rem] leading-relaxed" data-id="element-207">
              Zodra één concurrent in jouw markt start, is de deur dicht. Check
              nu of jouw markt nog beschikbaar is.
            </p>
            <motion.a href="#gesprek" className="inline-flex items-center gap-3 bg-white text-brand-primary font-black text-[1.05rem] px-10 py-5 rounded-2xl transition-all duration-500" whileHover={{
            scale: 1.05,
            boxShadow: '0 12px 48px rgba(255, 255, 255, 0.3)'
          }} whileTap={{
            scale: 0.95
          }} data-id="element-208">
              Check beschikbaarheid
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>;
}